import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { DateService } from '@services/date.service';
import { SvgComponent } from '@components/svg.component';
import { AppRoutes } from '@helpers/app-routes';
import { AuthService } from '@services/auth.service';
import { ConnectionStatusService } from '@services/connection-status.service';
import { OfflineNotificationService } from '@services/offline-notification.service';
import { ensureOnlineGuard } from '@guard/ensure-online.guard';
import { MealmomentService } from '@services/meal-moment.service';
import { TerminalService } from '@services/terminal.service';
import { CapitalizePipe } from '@pipes/capitalize.pipe';
import { EnterpriseService } from '@services/enterprise.service';
import { UserModel } from '@models/user/user.model';
import { ModalUserPreferencesComponent } from '@components/Modals/modal-user-preferences/modal-user-preferences.component';
import { ModalReauthComponent } from '@components/Modals/modal-reauth/modal-reauth.component';
import { ModalCardComponent } from '@components/Modals/modal-card/modal-card.component';
import { CompanyService } from '@services/company.service';
import { environment } from '@environments/environment';

const PUBLIC_PAGES: string[] = [
  AppRoutes.login,
  AppRoutes.resetPasswordRequest,
  AppRoutes.resetPassword,
];

@Component({
  selector: 'app-base-layout',
  templateUrl: './base.layout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    ModalUserPreferencesComponent,
    ModalReauthComponent,
    ModalCardComponent,
    SvgComponent,
    TranslateModule,
    CapitalizePipe,
  ],
})
export class BaseLayoutComponent implements OnInit {
  @ViewChild('body', { static: true })
  private readonly bodyElement!: ElementRef<HTMLElement>;

  protected readonly buildDate = environment.buildDate;
  protected readonly lastPipeline = environment.lastPipeline;
  protected readonly version = environment.version;

  @HostListener('document:click')
  closeUserDropdown(): void {
    this.showUserDropdown.set(false);
  }

  protected headerConfig: HeaderConfig = {
    showLocation: true,
    showCashRegister: true,
    showStatus: true,
    showUserMenu: true,
    showDeliveryButton: true,
    showSettingsButton: true,
    showPreferencesButton: true,
    locationText: null,
    cashRegisterText: null,
  };

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly dateService = inject(DateService);
  private readonly mealMomentService = inject(MealmomentService);
  private readonly terminalService = inject(TerminalService);
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly connectionStatusService = inject(ConnectionStatusService);
  private readonly companyService = inject(CompanyService);
  private readonly offlineNotification = inject(OfflineNotificationService);

  protected readonly statusText = computed(() => this.connectionStatusService.status());
  protected readonly authenticatedUser = toSignal(this.authService.user$, {
    initialValue: this.authService.getCurrentUser(),
  });
  protected readonly user = computed<UserModel | null>(() => {
    const authenticatedUser = this.authenticatedUser();
    if (authenticatedUser) {
      return authenticatedUser;
    }

    const terminal = this.terminalService.currentTerminal();
    if (!this.isCheckinMode() || !terminal) {
      return null;
    }

    return {
      id: 0,
      remote_id: null,
      firstname: terminal.label,
      lastname: '',
      username: terminal.label,
      roles: [],
      email: '',
      badge_number: null,
      is_enabled: true,
    };
  });
  protected readonly showUserDropdown = signal(false);
  protected readonly showCloseSessionButton = signal(false);
  protected readonly showPreferencesModal = signal(false);
  protected readonly showReauthModal = signal(false);
  protected readonly showTenantMismatchModal = this.terminalService.hasTenantMismatch;
  protected readonly isCheckinMode = computed(
    () => this.terminalService.currentTerminal()?.is_check_in ?? false,
  );

  #pendingReauthAction: (() => void) | null = null;

  protected readonly mealMoment = computed(
    () => this.mealMomentService.currentMealMoment()?.name ?? null,
  );

  protected readonly enterpriseName = computed(
    () => this.enterpriseService.currentEnterprise()?.name ?? null,
  );

  protected readonly date = computed(() =>
    this.dateService.format(new Date(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  );

  constructor() {
    effect(() => {
      if (this.connectionStatusService.status() !== 'common.offline') {
        return;
      }

      untracked(() => {
        if (this.isOnlineOnlyPage()) {
          this.offlineNotification.notifyFeatureUnavailable();
          void this.router.navigate([AppRoutes.login]);
        }
      });
    });
  }

  ngOnInit(): void {
    this.syncHeaderConfig();
    if (!this.isPublicPage()) {
      this.initTerminal();
    }

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.syncHeaderConfig();
      });
  }

  protected onTenantMismatchConfirm(): void {
    this.authService.clearCheckInSession();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = `/${AppRoutes.scanPage}`;
  }

  protected onTenantMismatchCancel(): void {
    this.terminalService.clearTenantMismatch();
    this.authService.logout();
    void this.router.navigate([AppRoutes.scanPage]);
  }

  private isOnlineOnlyPage(): boolean {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (snapshot) {
      if (snapshot.routeConfig?.canActivate?.includes(ensureOnlineGuard)) {
        return true;
      }
      snapshot = snapshot.firstChild;
    }

    return false;
  }

  private isPublicPage(): boolean {
    return PUBLIC_PAGES.some((route) => this.router.url.includes(route));
  }

  private initTerminal(): void {
    this.showCloseSessionButton.set(
      !(
        this.router.url.includes(AppRoutes.openSession) ||
        this.router.url.includes(AppRoutes.closeSession)
      ),
    );
    this.terminalService.getTerminals().subscribe();

    const enterpriseId = this.terminalService.currentTerminal()?.enterprise_id;
    if (enterpriseId) {
      this.enterpriseService.getEnterprise(enterpriseId).subscribe();
    }

    this.mealMomentService.getMealmoments().subscribe();
    this.companyService.getCompany().subscribe();
  }

  protected toggleUserDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showUserDropdown.update((v) => !v);
  }

  protected logout(): void {
    this.#requireReauthIfCheckin(() => {
      this.authService.logout();
      void this.router.navigate([AppRoutes.login]);
    });
  }

  protected closeSession(): void {
    this.#requireReauthIfCheckin(() => {
      void this.router.navigate([AppRoutes.closeSession]);
    });
  }

  protected listOrders(): void {
    this.#requireReauthIfCheckin(() => {
      void this.router.navigate([AppRoutes.orderList]);
    });
  }

  protected openSettings(): void {
    this.#requireReauthIfCheckin(() => {
      void this.router.navigate([AppRoutes.settings]);
    });
  }

  protected togglePreferencesModal(): void {
    this.showPreferencesModal.update((v) => !v);
  }

  protected closePreferencesModal(): void {
    this.showPreferencesModal.set(false);
  }

  #requireReauthIfCheckin(action: () => void): void {
    if (!this.isCheckinMode() || !this.router.url.includes(AppRoutes.scanPage)) {
      action();
      return;
    }
    this.#pendingReauthAction = action;
    this.showReauthModal.set(true);
  }

  protected onReauthSuccess(): void {
    this.showReauthModal.set(false);
    this.#pendingReauthAction?.();
    this.#pendingReauthAction = null;
  }

  protected onReauthCancel(): void {
    this.showReauthModal.set(false);
    this.#pendingReauthAction = null;
  }

  get statusClass() {
    switch (this.statusText()) {
      case 'common.online':
        return 'bg-vert-1';
      case 'common.offline':
        return 'bg-rose-1';
    }
  }

  get statusIconColor() {
    switch (this.statusText()) {
      case 'common.online':
        return 'bg-vert-3';
      case 'common.offline':
        return 'bg-rose-3';
    }
  }

  getUserDisplayName(user: UserModel): string {
    if (user.firstname && user.lastname) {
      const lastInitial = user.lastname.trim().charAt(0).toUpperCase();
      return `${user.firstname} ${lastInitial}.`;
    }
    const fullName = `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim();
    return fullName || user.username || 'Utilisateur';
  }

  getUserInitials(user: UserModel): string {
    const initials = [user.firstname, user.lastname]
      .filter((value): value is string => Boolean(value))
      .map((value) => value.trim().charAt(0))
      .join('')
      .toUpperCase();

    if (initials) {
      return initials.slice(0, 2);
    }

    return (user.username?.trim().charAt(0) || 'U').toUpperCase();
  }

  getCenterItems(): HeaderSecondaryItem[] {
    const items: HeaderSecondaryItem[] = [];

    if (this.headerConfig.showLocation && this.headerConfig.locationText) {
      items.push('location');
    }

    if (this.headerConfig.showCashRegister && this.headerConfig.cashRegisterText) {
      items.push('cashRegister');
    }

    if (this.headerConfig.showStatus) {
      items.push('status');
    }

    return items;
  }

  openHomePage(): void {
    void this.router.navigate([AppRoutes.scanPage]);
  }

  private syncHeaderConfig(): void {
    const routeHeader = this.getCurrentRouteData()['header'] as Partial<HeaderConfig> | undefined;

    this.headerConfig = {
      ...this.headerConfig,
      cashRegisterText:
        this.terminalService.currentTerminal()?.label ?? this.headerConfig.cashRegisterText,
      ...(routeHeader ?? {}),
    };
  }

  private getCurrentRouteData(): Record<string, unknown> {
    let currentRoute = this.activatedRoute;

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    return currentRoute.snapshot.data;
  }
}
